import React, {useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {ReactComponent as CheckIcon} from 'assets/svg/check2.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Modal} from 'components/ui-kit/Modal';
import {Spinner} from 'components/ui-kit/Spinner';
import {breakpoint} from 'theme';
import {FormattedLabelsForm} from 'types';
import {useMatchMedia} from 'utils/custom-hooks';

import {
    ExportError,
    ExportStatus,
    ModalContent,
    OptionCard,
    OptionCards,
    OptionCheck,
    OptionLabel,
    StartButtonWrap,
} from './styled';

interface Props {
    show: boolean;
    onClose: () => void;
    onConfirm: (format: FormattedLabelsForm['pageFormat']) => void;
    specificFormat?: boolean;
    choosePageFormat?: boolean;
    busy?: boolean;
    canResume?: boolean;
    phase?: string;
    error?: string;
}

export const OptionCheckComponent = () => (
    <OptionCheck>
        <Icon style={{marginLeft: '1px'}} source={CheckIcon} width={9} height={9} />
    </OptionCheck>
);

export const PrintModal: React.FC<Props> = ({
    show,
    onClose,
    onConfirm,
    specificFormat,
    choosePageFormat = true,
    busy = false,
    canResume = false,
    phase = '',
    error = '',
}) => {
    const {t: translation} = useTranslation();
    const mapping = useMemo<Partial<Record<FormattedLabelsForm['pageFormat'], string>>>(
        () => ({
            LETTER: 'letter',
            A4: 'a4',
            ...(!specificFormat && {
                STICKER_LARGE_H: 'largeHorizontal',
                STICKER_LARGE_V: 'largeVertical',
                STICKER_SMALL_H: 'smallHorizontal',
                STICKER_SMALL_V: 'smallVertical',
            }),
        }),
        [specificFormat],
    );
    const t = (key: string) => translation('pages.orders.print.' + key);
    const [selected, setSelected] = useState<FormattedLabelsForm['pageFormat']>('LETTER');
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.tablet})`);

    useEffect(() => {
        if (show && !canResume) setSelected('LETTER');
    }, [show, canResume]);

    return (
        <Modal
            className="pick-print-format-options-modal"
            show={show}
            onClose={onClose}
            closable={!busy}
            hideCloseButton={busy}
            modalWidth={isMobile ? 1 : 0}
            title={t(choosePageFormat ? 'title' : 'submit')}
            footer={
                <StartButtonWrap>
                    <Button variant={ButtonVariant.Text} disabled={busy} onClick={onClose}>
                        {t('cancel')}
                    </Button>
                    <Button disabled={busy} aria-busy={busy} onClick={() => onConfirm(selected)}>
                        {t('submit')}
                    </Button>
                </StartButtonWrap>
            }
        >
            <ModalContent>
                {error && <ExportError role="alert">{error}</ExportError>}
                {busy && phase && (
                    <ExportStatus role="status">
                        <Spinner aria-hidden="true" />
                        <span>{translation('dictionaries.orders.exportJob.' + phase)}</span>
                    </ExportStatus>
                )}
                {choosePageFormat && (
                    <OptionCards>
                        {Object.entries(mapping).map(([format, path]) => (
                            <OptionCard
                                key={format}
                                disabled={busy || canResume}
                                aria-pressed={selected === format}
                                $selected={selected === format}
                                onClick={() =>
                                    setSelected(format as FormattedLabelsForm['pageFormat'])
                                }
                                type="button"
                            >
                                <OptionLabel>
                                    <div>{t(path + '.label')}</div>
                                    <span>{t(path + '.size')}</span>
                                </OptionLabel>
                                {selected === format && <OptionCheckComponent />}
                            </OptionCard>
                        ))}
                    </OptionCards>
                )}
            </ModalContent>
        </Modal>
    );
};
